import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import Button from '../../components/ui/Button'
import type { ActiveAssetOption } from '../../types'

interface SelectAssetState {
  assets?: ActiveAssetOption[]
  qrCode?: string
}

// Una volqueta puede estar en N assets (distintas tarifas por cargue/descargue).
// El transportista elige cuál aplica antes de abrir el form de viaje.
const SelectAsset: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state || {}) as SelectAssetState
  const assets = state.assets ?? []
  const qrCode = state.qrCode

  useEffect(() => {
    if (assets.length === 0) navigate('/dashboard', { replace: true })
  }, [assets.length, navigate])

  const pick = (asset: ActiveAssetOption) => {
    const params = new URLSearchParams()
    if (qrCode) params.set('qr', qrCode)
    params.set('transportAssetId', String(asset.assetId))
    // dealId/operationId al query también → sobreviven un refresh (state se
    // pierde). Sin esto, tras refresh el form caería al contexto del asset[0].
    if (asset.dealId) params.set('dealId', String(asset.dealId))
    if (asset.operationId) params.set('operationId', String(asset.operationId))
    // pickedAsset en state → RegisterDumperTrip usa su hidratación exacta
    // (nextRemisionNumber/materialAssets del asset elegido, no del [0]).
    navigate(`/dumper-hourmeters/register?${params.toString()}`, {
      state: { pickedAsset: asset },
    })
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-retri-blue px-3 py-3 flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-white font-semibold">Elegir tarifa</h1>
      </header>

      <main className="max-w-md mx-auto p-4 space-y-3">
        <p className="text-sm text-gray-600">
          Esta volqueta tiene varias tarifas configuradas. Selecciona la que
          corresponde al viaje (según cargue/descargue).
        </p>

        {assets.map((asset) => (
          <button
            key={asset.assetId}
            type="button"
            onClick={() => pick(asset)}
            className="w-full text-left bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:border-retri-blue transition-colors"
          >
            <div className="space-y-1">
              <p className="font-semibold text-gray-800 flex items-center gap-2">
                {asset.groupName || asset.conditionIncomeName || `Tarifa #${asset.assetId}`}
                {asset.photoRequired ? (
                  <span className="text-[11px] font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">
                    Foto
                  </span>
                ) : null}
              </p>
              {asset.buildingName ? (
                <p className="text-sm text-gray-700">Obra: {asset.buildingName}</p>
              ) : null}
              {asset.licensePlate ? (
                <p className="text-xs text-gray-500">Placa: {asset.licensePlate}</p>
              ) : null}
              {asset.materialType ? (
                <p className="text-xs text-gray-500">Material: {asset.materialType}</p>
              ) : null}
              {asset.loadingZone || asset.unloadingZone ? (
                <p className="text-xs text-gray-500">
                  Cargue: {asset.loadingZone || '—'} · Descargue:{' '}
                  {asset.unloadingZone || '—'}
                </p>
              ) : null}
              {asset.dumperDealType ? (
                <p className="text-xs text-gray-500">
                  {asset.dumperDealType === 'MATERIAL_PLUS_TRANSPORT'
                    ? 'Material + transporte'
                    : 'Solo transporte'}
                </p>
              ) : null}
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
          </button>
        ))}
      </main>
    </div>
  )
}

export default SelectAsset
